import ArrowRight from '!svg-react-loader!./assets/arrowRight.svg';
import { SlRow, SlStatusChip, SlStatusChipColor, Text, useStacklineTheme } from '@stackline/ui';
import { border_grey } from 'src/components/BeaconV2Components/styleUtils';
import React, { useCallback } from 'react';
import {
  Direction,
  getColorForMetricChangeValue,
  getIconForMetricChangeForDirection
} from 'src/components/BeaconV2Components/utils/chartStyles';
import { SlColumn } from 'src/components/BeaconV2Components/Generic/SlColumn';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton';
import { useHistory } from 'src/utils/Hooks';
import { CARD_DIMENSIONS } from 'src/components/BeaconV2Components/GrowthMatrixScatterPlot/GrowthMatrixScatterPlot';

const CardSkeletonLoader = () => {
  return (
    <Flex flexDirection="column">
      <Flex>
        <SlSkeleton sx={{ borderRadius: '3px' }} variant="rectangular" width={40} height={25} />
      </Flex>

      <Flex marginTop="14px" gap={8}>
        <SlSkeleton variant="rectangular" width={40} height={40} />
        <Flex gap={4} flexDirection="column">
          <SlSkeleton variant="rectangular" sx={{ borderRadius: '2px' }} width={64} height={10} />
          <SlSkeleton variant="rectangular" sx={{ borderRadius: '2px' }} width={104} height={26} />
        </Flex>
      </Flex>

      <Flex marginTop="14px" gap={4} flexDirection="column">
        <SlSkeleton variant="rectangular" sx={{ borderRadius: '2px' }} width={104} height={14} />
        <SlSkeleton variant="rectangular" sx={{ borderRadius: '2px' }} width={140} height={16} />
      </Flex>

      <Flex marginTop="16px" gap={16}>
        <Flex flexDirection="column" gap={8}>
          <SlSkeleton variant="rectangular" sx={{ borderRadius: '2px' }} width={53} height={14} />
          <SlSkeleton variant="rectangular" sx={{ borderRadius: '2px' }} width={40} height={16} />
        </Flex>
        <Flex flexDirection="column" gap={8}>
          <SlSkeleton variant="rectangular" sx={{ borderRadius: '2px' }} width={64} height={14} />
          <SlSkeleton variant="rectangular" sx={{ borderRadius: '2px' }} width={40} height={16} />
        </Flex>
      </Flex>

      <Flex marginTop="16px">
        <SlSkeleton variant="rectangular" sx={{ borderRadius: '2px' }} width={81} height={14} />
      </Flex>
    </Flex>
  );
};

export interface ProductGrowthMatrixCardProps {
  imageUri: string;
  mainDisplayTitle: string;
  secondaryDisplayTitle: string;
  mainMetric: string;
  secondaryMetric: string;
  trafficIndex: string;
  conversionIndex: string;
  direction: Direction;
  statusCode: string;
  statusColor: SlStatusChipColor;
  detailsLink: string;
  icon?: string;
  disabledIcon?: string;
  loading: boolean;
  retailSales: number;
}

export const ProductGrowthMatrixCardInner = ({
  imageUri,
  mainDisplayTitle,
  secondaryDisplayTitle,
  mainMetric,
  secondaryMetric,
  trafficIndex,
  conversionIndex,
  direction,
  statusCode,
  statusColor,
  detailsLink,
  loading
}: ProductGrowthMatrixCardProps) => {
  const theme = useStacklineTheme();
  const history = useHistory();

  const renderContent = useCallback(() => {
    switch (loading) {
      case true:
        return <CardSkeletonLoader />;

      default:
        return (
          <SlColumn horizontalInset="none" verticalInset="none" widths="full" spacing={12}>
            <SlStatusChip color={statusColor} variant="large">
              {statusCode}
            </SlStatusChip>
            <SlRow spacing="sm">
              <img
                key={imageUri}
                src={imageUri}
                style={{ height: '40px', width: '40px' }}
                alt={secondaryDisplayTitle || mainDisplayTitle}
              />
              <SlColumn spacing="xs">
                <Text variant="subtitle3" truncateLines={2}>
                  {mainDisplayTitle}
                </Text>
                <Text variant="body4" truncateLines={2} title={secondaryDisplayTitle}>
                  {secondaryDisplayTitle}
                </Text>
              </SlColumn>
            </SlRow>
            <SlColumn spacing="xxs">
              <Text transform="uppercase" variant="subtitle4" color="secondary">
                Retail Sales
              </Text>
              <SlRow spacing="xs" verticalPosition="center">
                <Text variant="body2">{mainMetric}</Text>
                <SlRow verticalPosition="center" spacing="xxs">
                  {getIconForMetricChangeForDirection(direction)}
                  <Text variant="subtitle4" color={getColorForMetricChangeValue(null, direction)}>
                    {secondaryMetric}
                  </Text>
                </SlRow>
              </SlRow>
            </SlColumn>
            <SlRow spacing="md">
              <SlColumn spacing="xxs">
                <Text transform="uppercase" variant="subtitle4" color="secondary">
                  Traffic Index
                </Text>
                <Text variant="body2">{trafficIndex}</Text>
              </SlColumn>
              <SlColumn spacing="xxs">
                <Text transform="uppercase" variant="subtitle4" color="secondary">
                  Conversion Index
                </Text>
                <Text variant="body2">{conversionIndex}</Text>
              </SlColumn>
            </SlRow>
            <Text variant="subtitle4" link onClick={() => history.push(detailsLink)}>
              <SlRow verticalPosition="center" spacing="xs">
                {/* @ts-ignore */}
                <span style={{ fontWeight: theme.text.variants.subtitle4.fontWeight }}>View details</span>
                <ArrowRight
                  style={{
                    position: 'relative',
                    top: '1px'
                  }}
                />
              </SlRow>
            </Text>
          </SlColumn>
        );
    }
  }, [
    conversionIndex,
    detailsLink,
    direction,
    history,
    imageUri,
    loading,
    mainDisplayTitle,
    mainMetric,
    secondaryDisplayTitle,
    secondaryMetric,
    statusCode,
    statusColor,
    theme,
    trafficIndex
  ]);

  return (
    <div
      style={{
        border: `1px solid ${border_grey}`,
        borderRadius: '4px',
        width: `${CARD_DIMENSIONS.width}px`,
        height: `${CARD_DIMENSIONS.height}px`,
        backgroundColor: '#fff',
        padding: '12px'
      }}
    >
      {renderContent()}
    </div>
  );
};

export default ProductGrowthMatrixCardInner;
